import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  type User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase/config';
// TODO: re-enable real admin check after testing
import { getUserClaims } from '../utils/adminUtils';

type Claims = Record<string, unknown>;

type SignupFn = (
  email: string,
  password: string
) => ReturnType<typeof createUserWithEmailAndPassword>;
type LoginFn = (email: string, password: string) => ReturnType<typeof signInWithEmailAndPassword>;
type LogoutFn = () => ReturnType<typeof signOut> | Promise<void>;
type ResetPasswordFn = (email: string) => ReturnType<typeof sendPasswordResetEmail>;

interface AuthContextValue {
  currentUser: User | null;
  signup: SignupFn;
  login: LoginFn;
  logout: LogoutFn;
  resetPassword: ResetPasswordFn;
  authEnabled: boolean;
  isAdmin: boolean;
  userClaims: Claims;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authEnabled, setAuthEnabled] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userClaims, setUserClaims] = useState<Claims>({});

  // Safe auth functions that handle missing or invalid Firebase credentials
  function signup(email: string, password: string) {
    if (!authEnabled) {
      console.warn(
        'Firebase Authentication is not properly configured. Signup functionality is disabled.'
      );
      return Promise.reject(new Error('Authentication is not configured'));
    }
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email: string, password: string) {
    if (!authEnabled) {
      console.warn(
        'Firebase Authentication is not properly configured. Login functionality is disabled.'
      );
      return Promise.reject(new Error('Authentication is not configured'));
    }
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    if (!authEnabled) {
      console.warn(
        'Firebase Authentication is not properly configured. Logout functionality is disabled.'
      );
      return Promise.resolve();
    }
    return signOut(auth);
  }

  function resetPassword(email: string) {
    if (!authEnabled) {
      console.warn(
        'Firebase Authentication is not properly configured. Password reset functionality is disabled.'
      );
      return Promise.reject(new Error('Authentication is not configured'));
    }
    return sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    // Check if Firebase is properly configured
    try {
      // This is a safe check using try/catch to handle initialization issues
      const unsubscribe = onAuthStateChanged(
        auth,
        async user => {
          setCurrentUser(user);

          if (user && authEnabled) {
            // TEMPORARY: treat every signed-in user as an admin
            setIsAdmin(true);
            try {
              const claims = await getUserClaims();
              setUserClaims((claims as Claims) ?? {});
            } catch (error) {
              console.error('Error getting user claims:', error);
              setUserClaims({});
            }
          } else {
            // Clear admin status when user logs out
            setIsAdmin(false);
            setUserClaims({});
          }

          setLoading(false);
        },
        error => {
          console.error('Firebase auth state change error:', error);
          setAuthEnabled(false);
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up Firebase auth:', error);
      setAuthEnabled(false);
      setLoading(false);
      return () => {}; // Return empty cleanup function
    }
  }, [authEnabled]);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    authEnabled,
    isAdmin,
    userClaims,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
